import {FC} from 'react';
import {useTranslation} from 'react-i18next';

import IconDocumentDuplicate from '@/assets/images/icon-document-duplicate.svg?react';
import BorderedTextSeparator from '@/components/bordered-text-separator';
import FieldWrapper from '@/components/form/field-wrapper';
import {useNewDocumentCreation} from '@/components/new-document-creation/context';
import {useDocumentCreationActions} from '@/components/new-document-creation/dialog/context/document-create-actions.hook';
import {ListItem} from '@/components/new-document-creation/list-item';
import {useFormatFullName} from '@/hooks/use-format-fullname';
import {ContractHolder} from '@/repositories/beneva-api/endpoints/documents';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';

type Props = {
  contractHolders?: ContractHolder[];
};

export const CreateForRecentContractHolderStep: FC<Props> = ({contractHolders = []}) => {
  const {t} = useTranslation();
  const {createForContractHolderStepDisplayVariant} = useNewDocumentCreation();
  const formatFullName = useFormatFullName();
  const {onCreateForRecentContractHolder} = useDocumentCreationActions();

  return (
    <>
      {createForContractHolderStepDisplayVariant === 'verbose' && (
        <BorderedTextSeparator>
          {t(
            'components.new-document-creation.dialog.content.step-create-for-recent-contract-holder.section-separator-label',
          )}
        </BorderedTextSeparator>
      )}

      {createForContractHolderStepDisplayVariant === 'verbose' && (
        <h3 className="font-bold" data-testid="create-recent-contract-holder-title">
          {t('components.new-document-creation.dialog.content.step-create-for-recent-contract-holder.section-title')}
        </h3>
      )}

      <FieldWrapper
        identifier={`recent-contract-holder-field-wrapper`}
        showAssistiveText={false}
        label={
          createForContractHolderStepDisplayVariant === 'verbose'
            ? t(
                'components.new-document-creation.dialog.content.step-create-for-recent-contract-holder.section-description',
              )
            : undefined
        }
      >
        {contractHolders.map((contractHolder, index) => (
          <ListItem
            key={`recent-contract-holder-button-${contractHolder.firstName.toLowerCase()}-${contractHolder.lastName.toLowerCase()}-${index}`}
            data-testid={`recent-contract-holder-button-${contractHolder.firstName.toLowerCase()}-${contractHolder.lastName.toLowerCase()}`}
            onClick={() => {
              onCreateForRecentContractHolder(contractHolder);
            }}
            trackingKey={WEB_CLICK_TRACKING_KEY.SELECT_EXISTING_CONTRACT_HOLDER}
          >
            <IconDocumentDuplicate className="size-4 fill-violet" />
            {formatFullName(contractHolder.firstName, contractHolder.lastName)}
          </ListItem>
        ))}
      </FieldWrapper>
    </>
  );
};
